import { useState, useEffect, useCallback } from 'react';

// Get CSRF token from localStorage
const getCsrfToken = () => {
  return localStorage.getItem('csrf_token') || '';
};

const DatabaseCleanupModal = ({ isOpen, onClose, onDelete }) => {
  const [cleanupOptions, setCleanupOptions] = useState({
    deleteCancelled: true,
    deleteCancelledDays: 30,
    hideDashboardActivities: true,
    hideDashboardActivitiesDays: 30,
    deleteActivityLogs: true,
    deleteActivityLogsDays: 90
  });
  const [cleanupPreview, setCleanupPreview] = useState(null);
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const loadPreview = useCallback(async () => {
    setLoadingPreview(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${API_URL}/api/admin/database/cleanup-preview`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify(cleanupOptions)
      });

      const data = await response.json();
      
      if (!response.ok || data.error) {
        console.error('API Error:', data);
        throw new Error(data.message || 'Failed to load preview');
      }

      setCleanupPreview(data.data);
    } catch (err) {
      console.error('Failed to load preview:', err);
      setCleanupPreview({ cancelledCount: 0, activitiesCount: 0, logsCount: 0 });
    } finally {
      setLoadingPreview(false);
    }
  }, [cleanupOptions]);

  useEffect(() => {
    if (isOpen) {
      loadPreview();
    }
  }, [isOpen, loadPreview]);

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
      const response = await fetch(`${API_URL}/api/admin/database/cleanup`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': getCsrfToken()
        },
        body: JSON.stringify(cleanupOptions)
      });

      if (!response.ok) {
        throw new Error('Cleanup failed');
      }

      const data = await response.json();
      await onDelete(data.data.deleted);
      handleClose();
    } catch (err) {
      console.error('Cleanup failed:', err);
      alert('Nepodarilo sa vymazať záznamy');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleClose = () => {
    if (isDeleting) return;
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 300);
  };

  if (!isOpen) return null;

  return (
    <div className={`modal-overlay ${isClosing ? 'closing' : ''}`} onClick={handleClose}>
      <div className={`modal-content ${isClosing ? 'closing' : ''}`} style={{maxWidth: '600px'}} onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Čistenie záznamov databázy</h2>
          <button className="modal-close" onClick={handleClose} disabled={isDeleting}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <line x1="18" y1="6" x2="6" y2="18"/>
              <line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="modal-body">
          <div className="form-group">
            <label htmlFor="cleanup-cancelled" className="checkbox-label">
              <input
                id="cleanup-cancelled"
                type="checkbox"
                checked={cleanupOptions.deleteCancelled}
                onChange={(e) => setCleanupOptions({...cleanupOptions, deleteCancelled: e.target.checked})}
                disabled={isDeleting}
              />
              <span>Zrušené rezervácie</span>
            </label>
            {cleanupOptions.deleteCancelled && (
              <div className="nested-options">
                <label className="text-muted" style={{fontSize: '13px'}}>Staršie ako:</label>
                <select 
                  className="input-field"
                  value={cleanupOptions.deleteCancelledDays}
                  onChange={(e) => setCleanupOptions({...cleanupOptions, deleteCancelledDays: parseInt(e.target.value)})}
                  disabled={isDeleting}
                >
                  <option value="7">7 dní</option>
                  <option value="14">14 dní</option>
                  <option value="30">30 dní</option>
                  <option value="60">60 dní</option>
                  <option value="90">90 dní</option>
                </select>
                {cleanupPreview && (
                  <div className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                    K zmazaniu: <strong>{cleanupPreview.cancelledCount}</strong> záznamov
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cleanup-activities" className="checkbox-label">
              <input
                id="cleanup-activities"
                type="checkbox"
                checked={cleanupOptions.hideDashboardActivities}
                onChange={(e) => setCleanupOptions({...cleanupOptions, hideDashboardActivities: e.target.checked})}
                disabled={isDeleting}
              />
              <span>Posledné aktivity (v okne Prehľad)</span>
            </label>
            {cleanupOptions.hideDashboardActivities && (
              <div className="nested-options">
                <label className="text-muted" style={{fontSize: '13px'}}>Staršie ako:</label>
                <select 
                  className="input-field"
                  value={cleanupOptions.hideDashboardActivitiesDays}
                  onChange={(e) => setCleanupOptions({...cleanupOptions, hideDashboardActivitiesDays: parseInt(e.target.value)})}
                  disabled={isDeleting}
                >
                  <option value="3">3 dni</option>
                  <option value="7">7 dní</option>
                  <option value="14">14 dní</option>
                  <option value="30">30 dní</option>
                  <option value="60">60 dní</option>
                  <option value="90">90 dní</option>
                </select>
                {cleanupPreview && (
                  <div className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                    Z feedu sa skryje: <strong>{cleanupPreview.activitiesCount}</strong> aktivít
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="form-group">
            <label htmlFor="cleanup-logs" className="checkbox-label">
              <input
                id="cleanup-logs"
                type="checkbox"
                checked={cleanupOptions.deleteActivityLogs}
                onChange={(e) => setCleanupOptions({...cleanupOptions, deleteActivityLogs: e.target.checked})}
                disabled={isDeleting}
              />
              <span>História zmien</span>
            </label>
            {cleanupOptions.deleteActivityLogs && (
              <div className="nested-options">
                <label className="text-muted" style={{fontSize: '13px'}}>Staršie ako:</label>
                <select 
                  className="input-field"
                  value={cleanupOptions.deleteActivityLogsDays}
                  onChange={(e) => setCleanupOptions({...cleanupOptions, deleteActivityLogsDays: parseInt(e.target.value)})}
                  disabled={isDeleting}
                >
                  <option value="30">30 dní</option>
                  <option value="60">60 dní</option>
                  <option value="90">90 dní</option>
                  <option value="180">180 dní</option>
                  <option value="365">365 dní</option>
                </select>
                {cleanupPreview && (
                  <div className="text-muted" style={{fontSize: '13px', marginTop: '8px'}}>
                    K zmazaniu: <strong>{cleanupPreview.logsCount}</strong> záznamov
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="alert alert-warning">
            <span className="alert-icon" aria-hidden="true">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"/>
                <line x1="12" y1="9" x2="12" y2="13"/>
                <line x1="12" y1="17" x2="12.01" y2="17"/>
              </svg>
            </span>
            <span>
              <strong>UPOZORNENIE:</strong> Vybrané položky sa vyčistia. Skryté aktivity sa prestanú zobrazovať v Prehľade.
            </span>
          </div>
        </div>

        <div className="modal-footer">
          <button 
            className="btn btn-secondary"
            onClick={handleClose}
            disabled={isDeleting}
          >
            Zrušiť
          </button>
          <button 
            className="btn btn-danger"
            onClick={handleDelete}
            disabled={isDeleting || !cleanupPreview}
          >
            {isDeleting ? 'Čistím...' : 'Spustiť čistenie'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default DatabaseCleanupModal;
